import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useInviteCodes } from '@/hooks/useInviteCodes';
import { supabase } from '@/integrations/supabase/client';
import { Ticket, Loader2, CheckCircle, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';
import { useDateFormat } from '@/contexts/DateFormatContext';

// Local storage key for invite code (shared with Auth.tsx, localStorage persists across tabs)
const INVITE_CODE_STORAGE_KEY = 'momentumeq_pending_invite_code';
const INVITE_CODE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

interface InviteCodeStepProps {
  initialCode?: string;
  onComplete: () => void;
  onSkip: () => void;
}

// Normalize code: uppercase, remove spaces
const normalizeCode = (input: string): string => {
  return input.replace(/\s/g, '').toUpperCase();
};

// Validate code format
const validateCode = (code: string): { valid: boolean; error?: string } => {
  const normalized = normalizeCode(code);

  if (normalized.length < 8) {
    return { valid: false, error: 'Code must be at least 8 characters' };
  }

  if (normalized.length > 20) {
    return { valid: false, error: 'Code must be at most 20 characters' };
  }

  if (!/^[A-Z0-9_-]+$/.test(normalized)) {
    return { valid: false, error: 'Code can only contain letters, numbers, dashes, and underscores' };
  }

  return { valid: true };
};

// Get stored invite code from localStorage (returns null if expired or not present)
const getStoredInviteCode = (): string | null => {
  try {
    const raw = localStorage.getItem(INVITE_CODE_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    // Check expiry
    if (Date.now() - data.timestamp > INVITE_CODE_EXPIRY_MS) {
      localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
      return null;
    }
    return data.code || null;
  } catch {
    return null;
  }
};

// Clear stored invite code from localStorage
const clearStoredInviteCode = () => {
  try {
    localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
};

export const InviteCodeStep = ({ initialCode, onComplete, onSkip }: InviteCodeStepProps) => {
  const { toast } = useToast();
  const { redeemCode } = useInviteCodes();
  const { formatDate } = useDateFormat();
  
  // Check for stored code from signup flow, then fall back to initialCode prop
  const storedCode = getStoredInviteCode();
  const effectiveInitialCode = storedCode || initialCode;
  
  const [code, setCode] = useState(effectiveInitialCode ? normalizeCode(effectiveInitialCode) : '');
  const [redeeming, setRedeeming] = useState(false);
  const [success, setSuccess] = useState<{
    tier: string;
    expires_at: string;
  } | null>(null);
  const [prefilledValidation, setPrefilledValidation] = useState<
    'idle' | 'validating' | 'valid' | 'invalid'
  >(effectiveInitialCode ? 'validating' : 'idle');
  const [prefilledError, setPrefilledError] = useState<string | null>(null);

  // Validate pre-filled code on mount
  useEffect(() => {
    if (!effectiveInitialCode) return;
    const normalized = normalizeCode(effectiveInitialCode);
    setCode(normalized);

    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc('validate_invite_code', { p_code: normalized });
        if (cancelled) return;
        const result = typeof data === 'string' ? JSON.parse(data) : data;
        if (error || !result?.valid) {
          setPrefilledValidation('invalid');
          const errorMap: Record<string, string> = {
            invalid_code: 'This invite code is not recognized.',
            code_expired: 'This invite code has expired.',
            code_exhausted: 'This invite code has reached its maximum number of uses.',
          };
          setPrefilledError(errorMap[result?.error] || 'This invite code is no longer valid.');
        } else {
          setPrefilledValidation('valid');
        }
      } catch {
        if (!cancelled) {
          // On network error, don't show misleading success — just stay neutral
          setPrefilledValidation('idle');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveInitialCode]);

  const handleCodeChange = (value: string) => {
    setCode(normalizeCode(value));
    setPrefilledValidation('idle');
    setPrefilledError(null);
  };

  const handleRedeem = async () => {
    const normalized = normalizeCode(code);
    const validation = validateCode(normalized);
    
    if (!validation.valid) {
      toast({
        title: 'Invalid Code',
        description: validation.error,
        variant: 'destructive',
      });
      return;
    }

    setRedeeming(true);
    try {
      const result = await redeemCode(normalized);

      if (result.success) {
        // Clear stored code on successful redemption
        clearStoredInviteCode();
        
        setSuccess({
          tier: result.tier || 'beta_pro',
          expires_at: result.expires_at || '',
        });
        toast({
          title: '🎉 Welcome to the beta!',
          description: `You now have Pro access until ${result.expires_at ? formatDate(result.expires_at, 'long') : 'your subscription ends'}.`,
        });
      } else {
        toast({
          title: 'Redemption Failed',
          description: result.message,
          variant: 'destructive',
        });
      }
    } catch (err: any) {
      toast({
        title: 'Something went wrong',
        description: 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setRedeeming(false);
    }
  };

  // Success state - show celebration and continue button
  if (success) {
    return (
      <Card className="card-elevated border-border/50 max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mb-4 animate-in zoom-in-50 duration-300">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="font-display text-2xl">Welcome to Pro!</CardTitle>
          <CardDescription>
            Your invite code has been successfully redeemed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <p className="text-lg font-medium">
              You now have {success.tier === 'pro' ? 'Pro' : 'Beta Pro'} access!
            </p>
            {success.expires_at && (
              <p className="text-sm text-muted-foreground mt-1">
                Valid until {formatDate(success.expires_at, 'long')}
              </p>
            )}
          </div>

          <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 text-left">
            <p className="text-sm font-medium mb-2">Your Pro benefits:</p>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Unlimited goals and habits
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                AI Career Coach insights
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                90-day analytics history
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                Priority support
              </li>
            </ul>
          </div>

          <Button 
            onClick={onComplete} 
            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
          >
            Continue to MomentumEQ
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Input state
  return (
    <Card className="card-elevated border-border/50 max-w-md mx-auto">
      <CardHeader className="text-center">
        <div className="mx-auto w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <Ticket className="w-6 h-6 text-primary" />
        </div>
        <CardTitle className="font-display text-2xl">Do you have an invite code?</CardTitle>
        <CardDescription>
          Beta users get full Pro access free during our launch period.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {prefilledValidation === 'validating' && (
          <div className="p-3 rounded-lg bg-muted/50 border border-border text-center">
            <p className="text-sm text-muted-foreground font-medium flex items-center justify-center gap-2">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Verifying your invite code...
            </p>
          </div>
        )}
        {prefilledValidation === 'valid' && (
          <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-center">
            <p className="text-sm text-green-600 dark:text-green-400 font-medium">
              ✓ Your invite code is valid!
            </p>
          </div>
        )}
        {prefilledValidation === 'invalid' && (
          <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-center">
            <p className="text-sm text-destructive font-medium flex items-center justify-center gap-2">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {prefilledError}
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Input
            placeholder="Enter invite code..."
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && code.length >= 8 && handleRedeem()}
            className="font-mono text-center text-lg tracking-widest uppercase"
            disabled={redeeming}
            maxLength={20}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <p className="text-xs text-muted-foreground text-center">
            Codes are case-insensitive. Enter with or without dashes.
          </p>
        </div>

        <div className="space-y-3">
          <Button
            onClick={handleRedeem}
            disabled={code.length < 8 || redeeming}
            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90 transition-opacity"
          >
            {redeeming ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Redeeming...
              </>
            ) : (
              <>
                Redeem & Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          <Button
            variant="ghost"
            onClick={onSkip}
            disabled={redeeming}
            className="w-full"
          >
            Skip — Continue without a code
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default InviteCodeStep;
